/**
 * PostList 工具函数
 * 用于帖子列表的分组、排序等操作
 */

import type { CloudPost } from '../types';

// 排序类型
export type SortBy = 'blogger' | 'time' | 'likes' | 'collects';

// 格式化数字显示
export const formatCount = (count: number | null | undefined): string => {
  if (count == null) return '0';
  if (count >= 10000) {
    return (count / 1000).toFixed(0) + 'k';
  }
  if (count >= 1000) {
    return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return count.toString();
};

// 增强订阅人数显示（用于显示更有吸引力的数字）
export const enhanceSubscriberCount = (realCount: number, id: string | undefined): number => {
  if (!id) return realCount;
  const seed = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const randomBoost = 5 + (seed % 96);
  return realCount + randomBoost;
};

// 按博主分组帖子
export const groupPostsByBlogger = (posts: CloudPost[]): Record<string, CloudPost[]> => {
  const grouped: Record<string, CloudPost[]> = {};
  posts.forEach(post => {
    const bloggerId = post.isPageClip ? 'pageclip' : (post.bloggerId || 'pageclip');
    if (!grouped[bloggerId]) {
      grouped[bloggerId] = [];
    }
    grouped[bloggerId].push(post);
  });
  return grouped;
};

// 排序博主组
export const sortBloggerGroups = (
  bloggerIds: string[],
  grouped: Record<string, CloudPost[]>,
  sortBy: SortBy
): string[] => {
  if (sortBy === 'blogger') {
    return [...bloggerIds].sort((a, b) => {
      if (a === 'pageclip') return -1;
      if (b === 'pageclip') return 1;
      const aFollowers = grouped[a][0]?.blogger?.followersCount || 0;
      const bFollowers = grouped[b][0]?.blogger?.followersCount || 0;
      return bFollowers - aFollowers;
    });
  } else {
    return [...bloggerIds].sort((a, b) => {
      if (a === 'pageclip') return -1;
      if (b === 'pageclip') return 1;
      const aTime = Math.max(...grouped[a].map(p => new Date(p.createdAt || 0).getTime()));
      const bTime = Math.max(...grouped[b].map(p => new Date(p.createdAt || 0).getTime()));
      return bTime - aTime;
    });
  }
};

// 排序单独剪藏的帖子（用于本地剪藏簿，支持置顶）
export const sortPageClipPosts = (posts: CloudPost[]): CloudPost[] => {
  return [...posts].sort((a, b) => {
    // 1. 置顶优先
    if (a.isPinned !== b.isPinned) {
      return b.isPinned ? 1 : -1;
    }
    // 2. 按时间倒序
    const timeA = new Date(a.createdAt || 0).getTime();
    const timeB = new Date(b.createdAt || 0).getTime();
    return timeB - timeA;
  });
};
